import { types as api, sdk } from '@mesa-labs/mesa-api';
import { VendorApi } from '@mesa-labs/mesa-api/dist/apis/vendor';
import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';

import { prepareHeaders, useApi } from './api';

export const partnersApi = createApi({
  reducerPath: 'partnersApi',
  baseQuery: fetchBaseQuery({
    baseUrl: CONFIG.api.vendorUrl,
    prepareHeaders,
  }),
  tagTypes: ['Partner Summaries', 'Partner Team Members', 'Partner Permission Groups'],
  endpoints: (build) => ({
    getAllPartners: build.query<readonly api.PartnerSummaryResponse[], void>({
      async queryFn() {
        const vendorApi = useApi((options) => new VendorApi(CONFIG.api.vendorUrl, options));
        const data = await vendorApi.getAllPartners<api.PartnerSummaryResponse>();
        return { data };
      },
      providesTags: ['Partner Summaries'],
    }),
    getPartnerTeamMembers: build.query<readonly sdk.UserAccountResponse[], { partnerId: api.Partners }>({
      async queryFn({ partnerId }) {
        const vendorApi = useApi((options) => new VendorApi(CONFIG.api.vendorUrl, options));
        const { data } = await vendorApi.getAllPartnerUserAccounts<sdk.UserAccountResponse>(partnerId);
        return { data };
      },
      providesTags: (result, error, parameters) => (result
        ? [{ type: 'Partner Team Members', id: parameters.partnerId }]
        : []),
    }),
    addPartnerTeamMember: build.mutation<sdk.UserAccountResponse, sdk.CreateUserAccountRequest & { partnerId: api.Partners }>({
      async queryFn({ partnerId, ...body }) {
        const vendorApi = useApi((options) => new VendorApi(CONFIG.api.vendorUrl, options));
        const data = await vendorApi.createPartnerUserAccount<sdk.UserAccountResponse>(partnerId, body);
        return { data };
      },
      invalidatesTags: (result, _error, parameters) => [
        { type: 'Partner Team Members', id: parameters.partnerId },
        { type: 'Partner Summaries', id: parameters.partnerId },
      ],
    }),
    deactivatePartnerTeamMember: build.mutation<void, { partnerId: api.Partners; id: string }>({
      async queryFn({ partnerId, id }) {
        const vendorApi = useApi((options) => new VendorApi(CONFIG.api.vendorUrl, options));
        await vendorApi.deactivatePartnerUserAccount(partnerId, id);
        return { data: undefined };
      },
      invalidatesTags: (result, _error, parameters) => [
        { type: 'Partner Team Members', id: parameters.partnerId },
        { type: 'Partner Summaries', id: parameters.partnerId },
      ],
    }),
    getPartnerPermissionGroups: build.query<readonly api.PartnerPermissionGroupResponse[], { partnerId: api.Partners }>({
      async queryFn({ partnerId }) {
        const vendorApi = useApi((options) => new VendorApi(CONFIG.api.vendorUrl, options));
        const data = await vendorApi.getPartnerPermissionGroups(partnerId);
        return { data };
      },
      providesTags: (result, error, parameters) => [{ type: 'Partner Permission Groups', id: parameters.partnerId }],
    }),
    putPartnerPermissionGroup: build.mutation<readonly api.PartnerPermissionGroupResponse[], { partnerId: api.Partners; group: string }>({
      async queryFn({ partnerId, group }) {
        const vendorApi = useApi((options) => new VendorApi(CONFIG.api.vendorUrl, options));
        const data = await vendorApi.putPartnerPermissionGroup(partnerId, group);
        return { data };
      },
      invalidatesTags: (result, _error, parameters) => [{ type: 'Partner Permission Groups', id: parameters.partnerId }],
    }),
    deletePartnerPermissionGroup: build.mutation<readonly api.PartnerPermissionGroupResponse[], { partnerId: api.Partners; group: string }>({
      async queryFn({ partnerId, group }) {
        const vendorApi = useApi((options) => new VendorApi(CONFIG.api.vendorUrl, options));
        const data = await vendorApi.deletePartnerPermissionGroup(partnerId, group);
        return { data };
      },
      invalidatesTags: (result, _error, parameters) => [{ type: 'Partner Permission Groups', id: parameters.partnerId }],
    }),
  }),
});

export const {
  useGetAllPartnersQuery,
  useGetPartnerTeamMembersQuery,
  useAddPartnerTeamMemberMutation,
  useDeactivatePartnerTeamMemberMutation,
  useGetPartnerPermissionGroupsQuery,
  usePutPartnerPermissionGroupMutation,
  useDeletePartnerPermissionGroupMutation,
} = partnersApi;
